import { useFacilityContext } from '@aspendental/shared-components-web';
import {
	BinaryChoice,
	BinaryChoiceSchema,
	DateTextInput,
	Form,
	MaskedPhoneNumberInput,
	MaskedPhoneNumberInputSchema,
	SchemaBuilder,
	TextInput,
	useHookForm,
} from '@aspendental/shared-forms-web';
import { Grid } from '@mui/material';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { FieldValues } from 'react-hook-form';

import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { AppointmentErrorMessages, DataLayerStepNames } from '@src/constants';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { dataLayer } from '@src/services/dataLayer';
import {
	PatientInformationSchema,
	useAppStore,
	useFormSectionStore,
	useGlobalUXStore,
	useGuarantorInformationFormStore,
	usePatientInformationFormStore,
	useReasonForVisitStore,
} from '@src/store';
import { ageEligibleForProcedure, guarantorInformationNeeded } from '@src/utils/business-logic';
import { dateInvalid } from '@src/utils/date-parser';

/**
 * Component: FullPatientInformationForm
 *
 * This form is used exclusively for the Motto flow, and manages guarantor and age restrictions
 * differently than standard flow.
 */
const FullPatientInformationForm = ({ formId }: { formId: string }) => {
	const completeActiveNode = useFormSectionStore((store) => store.completeActiveNode);
	const setGuarantorInfoNeeded = useFormSectionStore((store) => store.setGuarantorInfoNeeded);
	const setGuarantorRequired = useGuarantorInformationFormStore((store) => store.setRequired);
	const patientAge = usePatientInformationFormStore((store) => store.patientAge);
	const { mottoFlow } = useAppStore((store) => store);
	const { reasonForVisit } = useReasonForVisitStore((store) => store);
	const webFacilityDetails = useFacilityContext();
	const router = useRouter();
	const { locale } = router;

	const openDialogWithError = useGlobalUXStore((store) => store.openDialogWithError);

	const {
		email,
		setEmail,
		phonenumber,
		setPhonenumber,
		firstName,
		setFirstName,
		lastName,
		setLastName,
		dateOfBirth,
		setDateOfBirth,
		hasInsurance,
		setHasInsurance,
	} = usePatientInformationFormStore((store) => store);

	const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');

	const defaultValues = {
		firstName,
		lastName,
		dateOfBirth: initialValueDateOfBirth,
		email,
		phonenumber,
		hasInsurance,
	};

	const schema = SchemaBuilder([
		{ name: 'firstName', validation: PatientInformationSchema.firstName },
		{ name: 'lastName', validation: PatientInformationSchema.lastName },
		{ name: 'dateOfBirth', validation: PatientInformationSchema.dateOfBirth },
		{ name: 'email', validation: PatientInformationSchema.email },
		{ name: 'phonenumber', validation: MaskedPhoneNumberInputSchema.options.default },
		{ name: 'hasInsurance', validation: BinaryChoiceSchema.options.default },
	]);

	useEffect(() => {
		dataLayer({
			language_preference: locale,
			brand: mottoFlow ? 'mottoaligners' : 'aspendental',
			step_name: DataLayerStepNames.patientInformation,
			reason_for_visit: reasonForVisit,
			reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
			...(webFacilityDetails?.officeInfo?.code && {
				website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
			}),
			...(webFacilityDetails?.officeInfo?.name && {
				website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
			}),
		});
	}, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow]);

	const handleSubmit = (fields: FieldValues) => {
		setFirstName(fields.firstName);
		setLastName(fields.lastName);
		setDateOfBirth(fields.dateOfBirth);
		setEmail(fields.email);
		setPhonenumber(fields.phonenumber.replace(/[^\d]/g, ''));
		setHasInsurance(fields.hasInsurance);

		const age = patientAge() || 0;

		const ageOK = ageEligibleForProcedure(age, reasonForVisit as DigitalVisitReasonEnum);

		if (!ageOK) {
			openDialogWithError(AppointmentErrorMessages.onlineUnavailable);
			return;
		}

		const guarantorRequired = guarantorInformationNeeded(age);

		setGuarantorRequired(guarantorRequired);
		setGuarantorInfoNeeded(guarantorRequired);

		setTimeout(() => {
			completeActiveNode();
		}, 50);
	};

	const form = useHookForm({
		schema,
		defaultValues,
	});

	return (
		<Form form={form} onSubmit={handleSubmit} formId={formId}>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<TextInput name="firstName" label="First Name" data-test-id="patient-info-firstname" />
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="lastName" label="Last Name" data-test-id="patient-info-lastname" />
				</Grid>
				<Grid item xs={12} sm={8}>
					<DateTextInput label="Date of Birth" name="dateOfBirth" autoComplete="bday" required />
				</Grid>
				<Grid item xs={12} sm={8}>
					<TextInput
						name="email"
						label="Email Address"
						enforceAlphaNumeric={false}
						placeholder="email@domain.com"
					/>
				</Grid>
				<Grid item xs={12} sm={8}>
					<MaskedPhoneNumberInput name="phonenumber" label="Mobile Phone Number" />
				</Grid>
				<Grid item xs={12} sm={7}>
					<BinaryChoice
						groupLabel="Do you have dental insurance?"
						name="hasInsurance"
						defaultSelection={defaultValues.hasInsurance}
						variant="bodyLargeBook"
					/>
				</Grid>
			</Grid>
		</Form>
	);
};

export default FullPatientInformationForm;
